import React, { Component, useEffect, useRef, useState } from "react";

import {
  CCol,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CPagination
} from "@coreui/react";
import { Switch } from "@material-ui/core";
import {
  Copy,
  EllipsisVertical,
  FileText,
  Heart,
  HeartOff
} from "lucide-react";
import { connect, useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import {
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label
} from "reactstrap";
import { bindActionCreators } from "redux";

import defaultimagem from "./default.png";
import favoritesIcon from "./favorites.webp";
import rouletteIcon from "./roulette.webp";
import "./styles.css";
import { logout } from "../../../actions/baseActions";
import { setObj } from "../../../actions/noteActions";
import Breadcrumb from "../../../components/Breadcrumb";
import { ContentCard } from "../../../components/ContentCard";
import Loading from "../../../components/Loading";
import URL from "../../../config/api";
import axios from "../../../config/axios";

const HandsOn = ({ currentRoute, libraryMode, match, props }) => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [maxPages, setMaxPages] = useState(1);
  const [itemsCount, setItemsCount] = useState(0);
  const [word, setWord] = useState("");
  const [favorites, setFavorites] = useState([]);
  const [favoritesFilter, setFavoritesFilter] = useState(false);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState();
  const [typesLoading, setTypesLoading] = useState(true);
  const user = useSelector(state => state.user);
  const dispatch = useDispatch();
  const timeToUpdate = useRef(3);
  const intervalRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      await initContent();
      await getTypes();
      await getFavorites();
    };

    fetchData();
  }, [currentRoute]);

  useEffect(() => {
    getLos();
  }, [types]);

  useEffect(() => {
    timeToUpdate.current = 3;
    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      timeToUpdate.current -= 1;

      if (timeToUpdate.current === 0) {
        clearInterval(intervalRef.current);
        getLos();
      }
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [word]);

  useEffect(() => {
    const fetchLos = async () => {
      await getLos();
    };

    fetchLos();
  }, [favoritesFilter, categories]);

  const initContent = () => {
    setLoading(true);
    dispatch(setObj(16, currentRoute, currentRoute));
  };

  const getTypes = async () => {
    await axios
      .get(`${URL.api}content/type/list`, {
        headers: { authorization: `Bearer ${user.token}` }
      })
      .then(resp => {
        const handsOnTypes = resp.data.data.filter(type => type.hands_on);
        setTypes(handsOnTypes);
        setTypesLoading(false);
      });
  };

  const getFavorites = async () => {
    const response = await axios.get(`${URL.api}content/favorites/list`, {
      headers: {
        authorization: `Bearer ${user.token}`
      }
    });

    const favorites = response.data.data.map(favorite => favorite.lo_id);
    setFavorites(favorites);
  };

  const getLos = async () => {
    if (!types) {
      return;
    }

    setLoading(true);

    const filters = {
      word,
      // Adicionar todas as categorias que são hands on
      types: categories.length ? categories : types?.map(item => item.id),
      private: false,
      favorites: favoritesFilter
    };

    try {
      const resp = await axios.post(
        `${URL.api}content/los/listLosFilter?page=${currentPage}&page_size=16${
          libraryMode ? "&libraryMode=true" : ""
        }`,
        filters,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );

      if (!resp.data.data[0]) {
        setContent([]);
      }

      let sortedContent = resp.data.data[0]?.content;
      sortedContent?.sort((a, b) => b.id - a.id);

      resp.data.data[0].content = sortedContent;
      setContent(resp.data.data);
      setLoading(false);
      setCurrentPage(resp.data.meta.current_page);
      setMaxPages(resp.data.meta.last_page);
      setItemsCount(resp.data.meta.total);
    } catch (error) {
      setItemsCount(0);
      setLoading(false);
    }
  };

  const openRandomLO = async () => {
    try {
      axios
        .post(
          `${URL.api}content/los/random`,
          {
            types: categories.length ? categories : types?.map(item => item.id)
          },
          {
            headers: {
              Authorization: `Bearer ${user.token}`
            }
          }
        )
        .then(resp => {
          window.open(
            `/content/view/${resp.data.data.id}?title=${resp.data.data.name}`,
            "_blank"
          );
        });
    } catch {
      toastr.error("Erro ao abrir conteúdo sugerido!");
    }
  };

  const handleOpenPDF = content => {
    window.open(content.pdf_path, "_blank");
  };

  const checkIfContentIsNew = content => {
    const today = new Date();
    const contentDate = new Date(content.updated_at);
    const diffTime = Math.abs(today - contentDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays < 30;
  };

  const insertFavorite = async id => {
    await axios.post(
      `${URL.api}content/favorites/insert`,
      { lo_id: id },
      {
        headers: { authorization: `Bearer ${user.token}` }
      }
    );

    setFavorites(prevFavorites => [...prevFavorites, id]);
  };

  const deleteFavorite = async id => {
    await axios.delete(`${URL.api}content/favorites/deleteByLoId/${id}`, {
      headers: { authorization: `Bearer ${user.token}` }
    });

    setFavorites(prevFavorites =>
      prevFavorites.filter(favorite => favorite !== id)
    );
  };

  const contentItems = content.reduce(
    (acc, val) => [
      ...acc,
      ...val.content.map(item => ({ ...item, type_id: val.id }))
    ],
    []
  );

  return (
    <>
      <Breadcrumb current={currentRoute} />
      <div>
        <div className="hands-on-banner">
          <h1>Espaço mão na massa</h1>
          <h6>
            Porque a criatividade e o <br />
            aprendizado se completam no criar
          </h6>
        </div>
        <div className="row mb-3 d-flex align-items-center justify-content-end">
          <div className="handson-categorie-container col-md-6 col-12">
            <div className="w-100">
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText className="bg-white pr-1">
                    <i className="fa fa-search text-muted"></i>
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  placeholder="Pesquisar"
                  onChange={e => setWord(e.target.value)}
                  value={word}
                  id="searchInput"
                  type="search"
                  autoComplete="off"
                />
              </InputGroup>
            </div>
            <div className="d-flex flex-column flex-md-row mt-3">
              <div
                className="p-3 bg-white d-flex align-items-center justify-content-center flex-grow-1 border rounded handson-button"
                onClick={openRandomLO}
              >
                <img src={rouletteIcon} className="handson-button-icon" />
                <h3 className="mb-0 ml-1 handson-button-text">
                  O que fazer hoje?
                </h3>
              </div>
              <div
                className={`p-3 bg-white d-flex align-items-center justify-content-center flex-grow-1 mt-2 mt-md-0 ml-md-3 border rounded handson-button ${
                  favoritesFilter ? "handson-button-active" : ""
                }`}
                onClick={() => setFavoritesFilter(prev => !prev)}
              >
                <img src={favoritesIcon} className="handson-button-icon" />
                <h3 className="mb-0 ml-1 handson-button-text">Favoritos</h3>
              </div>
            </div>
          </div>
          <div className="handson-categorie-container col-md-6 col-12 text-center d-flex flex-nowrap overflow-auto">
            {typesLoading ? (
              <div className="h-100 flex-1 align-content-center">
                <Loading />
              </div>
            ) : (
              <>
                {types.map(categorie => {
                  const isIncluded = categories.includes(categorie.id);
                  return (
                    <div
                      key={categorie.id}
                      className={`handson-categorie-button d-flex flex-1 flex-column align-items-center border rounded ${
                        isIncluded ? "handson-button-active" : ""
                      }`}
                      onClick={() => {
                        setCategories(prev =>
                          isIncluded
                            ? prev.filter(item => item !== categorie.id)
                            : [...prev, categorie.id]
                        );
                      }}
                    >
                      <img
                        src={`${categorie.image || defaultimagem}`}
                        className="handson-categorie-button-image"
                      />
                      <p className="mt-2 handson-categorie-button-text">
                        {categorie.name}
                      </p>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        {loading ? (
          <Loading />
        ) : (
          <div>
            {content.length > 0 ? (
              <div>
                <div className="contentArea row">
                  {contentItems.map((contentItem, idx) => (
                    <CCol xs="6" md="6" lg="4" xl="3" key={`type-${idx}`}>
                      <ContentCard.Root
                        titleLink={`/content/view/${contentItem.id}?title=${contentItem.name}`}
                      >
                        <ContentCard.Banner
                          // fallbackColor={
                          //   types?.find(item => item.id === contentItem.type_id)
                          //     ?.color
                          // }
                          // fallbackIcon={
                          //   types?.find(item => item.id === contentItem.type_id)
                          //     .icon
                          // }
                          badge={checkIfContentIsNew(contentItem) && "Novo"}
                          image={contentItem.image}
                        />
                        <ContentCard.Main>
                          <ContentCard.Header title={contentItem.name}>
                            <ContentCard.Item>
                              <p className="text-muted">
                                {contentItem.discipline_name}
                                {contentItem.ability_code &&
                                  ` • (${contentItem.ability_code})`}
                              </p>
                            </ContentCard.Item>
                          </ContentCard.Header>
                        </ContentCard.Main>
                      </ContentCard.Root>
                      <CDropdown className="context">
                        <CDropdownToggle
                          caret={false}
                          className="contextToggle rounded-circle btn-sm p-3"
                        >
                          <EllipsisVertical size={18} />
                        </CDropdownToggle>
                        <CDropdownMenu placement="top-end">
                          <CDropdownItem
                            className="text-dark"
                            onClick={() => {
                              if (favorites.includes(contentItem.id)) {
                                deleteFavorite(contentItem.id);
                              } else {
                                insertFavorite(contentItem.id);
                              }
                            }}
                          >
                            {favorites.includes(contentItem.id) ? (
                              <HeartOff size={18} className="mr-2" />
                            ) : (
                              <Heart size={18} className="mr-2" />
                            )}
                            {favorites.includes(contentItem.id)
                              ? "Remover dos favoritos"
                              : "Adicionar aos favoritos"}
                          </CDropdownItem>
                          {contentItem.pdf_path && (
                            <CDropdownItem
                              className="text-dark"
                              onClick={() => {
                                handleOpenPDF(contentItem);
                              }}
                            >
                              <FileText size={18} className="mr-2" />
                              Baixar Arquivo
                            </CDropdownItem>
                          )}
                        </CDropdownMenu>
                      </CDropdown>
                    </CCol>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-muted p-4 d-flex justify-content-center">
                Nenhum conteúdo encontrado
              </div>
            )}
            <div className="d-flex row mt-4">
              <div className="col-4 text-muted">
                <span>Total: {itemsCount}</span>
              </div>
              <div className="col-8 col-md-8 col-sm-2 d-flex justify-content-end">
                <CPagination
                  activePage={currentPage}
                  pages={maxPages}
                  onActivePageChange={page =>
                    setCurrentPage(page, () => getLos())
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  user: state.user,
  general: state.general,
  profile: state.profile
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ logout, setObj }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(HandsOn);
